// Vue is imported explicitly rather than through Nuxt's auto-import so this file runs
// under the Nuxt-free Vitest setup (`vitest.config.ts`, environment `node`).
import { nextTick, onScopeDispose, watch, type Ref } from 'vue';
import { SHEET_CLOSE_DURATION_MS, SHEET_PANEL_EASING } from '~/composables/useSheetNavigation';

/**
 * What a sheet's swipe-to-dismiss is doing to the layers painted behind it, shared
 * between the sheet that is being dragged and `layout/ModalsBackdrop.vue`, which draws
 * the app-wide blurred overlays. Lives in `useModalsStore` (`sheetBackdrop`).
 *
 * While `active` is false nothing reads it: the layers render exactly as they always
 * have, which is what keeps desktop — whose modals use the same overlays — untouched.
 */
export interface SheetBackdropState {
    /** A gesture drives the layers, from the first move until the settle animation ends. */
    active: boolean;
    /** Finger down: the layers follow it 1:1, no transition. */
    swiping: boolean;
    /** 0 with the sheet at rest, 1 once it is dragged off-screen. */
    progress: number;
    /** Length of the animation that follows the release. */
    durationMs: number;
    /**
     * Whether the app-wide overlays follow too. Only when the dragged sheet is the only
     * modal open: dismissing it then reveals the page, so they must go with it. With
     * another modal underneath they stay, and only the sheet's own tint follows.
     */
    syncGlobal: boolean;
    /** Which sheet owns the gesture — only that one's own tint follows it. */
    owner: string | null;
}

export const createSheetBackdropState = (): SheetBackdropState => ({
    active: false,
    swiping: false,
    progress: 0,
    durationMs: 0,
    syncGlobal: false,
    owner: null,
});

/** The part of reka-ui's drawer root context this reads. See `ActionSheetSwipeBridge.vue`. */
export interface SheetSwipeSource {
    /**
     * reka-ui's normalised drag, pushed on every move: `displacement / (height + threshold)`,
     * clamped to 0..1. Subscribing to it is what spares this any per-frame work of its own —
     * no rAF loop, no layout read, no height to measure.
     */
    progress: { getSnapshot: () => number; subscribe: (listener: () => void) => () => void };
    isSwiping: Ref<boolean>;
    open: Ref<boolean>;
    /** The `--drawer-swipe-strength` reka writes on the sheet at release; 1 when absent. */
    readSwipeStrength: () => number;
}

export interface SheetSwipeBackdropOptions {
    /** Identifies this sheet in `SheetBackdropState.owner`. */
    ownerId: string;
    /** Read at the start of each gesture: see `SheetBackdropState.syncGlobal`. */
    syncGlobal: () => boolean;
    /** No settle animation — the finger is still followed, that is direct manipulation. */
    reducedMotion?: () => boolean;
}

/**
 * Drives the backdrop layers from a sheet's swipe-to-dismiss.
 *
 * The end of a gesture is handled here rather than read from reka-ui: when a swipe
 * finishes, reka zeroes its own progress without notifying its subscribers, so the value
 * it publishes simply freezes where the finger was lifted. Left alone, the backdrop would
 * stay half-faded behind a sheet that had snapped back open. The two outcomes are told
 * apart by the root's `open`: a dismissal has already asked the parent to close.
 *
 * After a dismissal the state is deliberately NOT released when the fade ends. reka keeps
 * the sheet's overlay mounted a little longer than that, and releasing took its inline
 * `opacity: 0` away while it was still on screen: it snapped back to its class opacity, 1,
 * for the frames left before unmount — a flicker, reproduced 9 times out of 12. The faded
 * state is kept instead and cleared when the sheet next opens, or when any modal opens
 * (`useModalsStore`), which is the first moment it could be seen again.
 */
export default function useSheetSwipeBackdrop(source: SheetSwipeSource, state: SheetBackdropState, options: SheetSwipeBackdropOptions) {
    let owns = false;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;

    const clearSettle = () => {
        if (settleTimer === null) return;
        clearTimeout(settleTimer);
        settleTimer = null;
    };

    const release = () => {
        settleTimer = null;
        if (!owns) return;
        owns = false;
        // Another sheet may have started its own gesture since; that state is not ours.
        if (state.owner !== options.ownerId) return;
        Object.assign(state, createSheetBackdropState());
    };

    // A gesture starts at rest. Reading reka's snapshot here would show wherever the
    // previous drag stopped, since that value is never reset (see above).
    const begin = () => {
        clearSettle();
        owns = true;
        Object.assign(state, {
            active: true,
            swiping: true,
            progress: 0,
            durationMs: 0,
            syncGlobal: options.syncGlobal(),
            owner: options.ownerId,
        });
    };

    const settle = async () => {
        // reka calls `onDismiss` → `onOpenChange(false)` before it drops the swiping flag,
        // but the parent's close only reaches the root's `open` on the next render.
        await nextTick();
        if (!owns) return;

        const dismissed = !source.open.value;
        const strength = dismissed ? source.readSwipeStrength() : 1;
        const durationMs = options.reducedMotion?.() ? 0 : Math.round(SHEET_CLOSE_DURATION_MS * strength);

        Object.assign(state, { swiping: false, durationMs, progress: dismissed ? 1 : 0 });

        // Snapping back leaves the layers at full opacity, so letting go of the inline
        // style afterwards changes nothing on screen. A dismissal keeps it: see above.
        if (!dismissed) settleTimer = setTimeout(release, durationMs);
    };

    // `sync`: reka raises the swiping flag and publishes the first progress in the same
    // call, so a deferred watcher would miss that first sample and then overwrite it.
    watch(
        source.isSwiping,
        (swiping, wasSwiping) => {
            if (swiping && !wasSwiping) begin();
            else if (!swiping && wasSwiping) settle();
        },
        { flush: 'sync' }
    );

    // The sheet coming back is when a dismissal's faded state must go, before its overlay
    // mounts again — otherwise it would open behind a transparent tint.
    watch(source.open, (isOpen) => {
        if (!isOpen) return;
        clearSettle();
        release();
    });

    const unsubscribe = source.progress.subscribe(() => {
        if (!owns || !source.isSwiping.value) return;
        state.progress = source.progress.getSnapshot();
    });

    onScopeDispose(() => {
        unsubscribe();
        clearSettle();
        release();
    });
}

/**
 * The inline style a backdrop layer takes while a gesture drives it, `undefined`
 * otherwise — so an idle layer carries no style of its own and keeps its classes' look.
 *
 * `opacity` only: it is composited, so neither the tint nor the blur underneath is
 * repainted. The blur keeps its radius; the page behind is frozen (`body.no-scroll`), so
 * the blurred texture is rendered once and merely faded.
 *
 * `animation-duration` rides along for the sheet's own overlay, whose fade-out is a CSS
 * animation — and an animation outranks an inline value — so it is made to last as long
 * as the sheet's own exit, which reka scales by the release strength.
 */
export const sheetBackdropStyle = (state: SheetBackdropState, applies: boolean): Record<string, string> | undefined => {
    if (!state.active || !applies) return undefined;

    const opacity = String(1 - state.progress);
    if (state.swiping) return { opacity, transition: 'none' };

    return {
        opacity,
        transition: `opacity ${state.durationMs}ms ${SHEET_PANEL_EASING}`,
        animationDuration: `${state.durationMs}ms`,
    };
};
